import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Target, TrendingUp, Settings, Crown, Zap, Flame, ShieldCheck, User, LogIn, LogOut, RotateCcw } from "lucide-react";
import { loadMemory, saveMemory, type UserMemory } from "@/lib/userMemory";
import { getProgressionLevel, getBuyingProfile, getActivitySummary, type ProgressionLevel } from "@/lib/watchlistStore";
import { usePlan } from "@/hooks/usePlan";
import type { PlanTier } from "@/lib/planGating";
import { getSavingsInsight, getStreak, getRiskAvoidance } from "@/lib/retentionEngine";
import { analyzeBehavior, getPersonalizationExplanation, getCategoryAffinity } from "@/lib/behaviorEngine";
import UpgradeModal from "@/components/UpgradeModal";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getProfile, updateProfile, getAccountStats, getReorderItems } from "@/lib/cloudServices";
import { toast } from "@/hooks/use-toast";
import AlertPreferences from "@/components/AlertPreferences";
import ReorderSection from "@/components/ReorderSection";

const levelColors: Record<ProgressionLevel, string> = {
  "New User": "text-muted-foreground",
  "Smart Shopper": "text-score-mid",
  "Deal Optimizer": "text-accent",
  "Decision Pro": "text-primary",
  Operator: "text-score-high",
};

const countries = [
  { code: "US", label: "United States", currency: "USD" },
  { code: "GB", label: "United Kingdom", currency: "GBP" },
  { code: "CA", label: "Canada", currency: "CAD" },
  { code: "AU", label: "Australia", currency: "AUD" },
  { code: "DE", label: "Germany", currency: "EUR" },
  { code: "FR", label: "France", currency: "EUR" },
  { code: "NL", label: "Netherlands", currency: "EUR" },
  { code: "IT", label: "Italy", currency: "EUR" },
  { code: "ES", label: "Spain", currency: "EUR" },
  { code: "AE", label: "UAE", currency: "AED" },
  { code: "JP", label: "Japan", currency: "JPY" },
  { code: "KR", label: "South Korea", currency: "KRW" },
  { code: "SG", label: "Singapore", currency: "SGD" },
  { code: "IN", label: "India", currency: "INR" },
];

const priorities = [
  { value: "quality", label: "Best overall" },
  { value: "price", label: "Lowest price" },
  { value: "speed", label: "Fast delivery" },
  { value: "safety", label: "Most reliable" },
];

const biases = [
  { value: "local", label: "Local first" },
  { value: "international", label: "International okay" },
  { value: "adaptive", label: "No preference" },
];

export default function ProfilePage() {
  const { user, isLoading: authLoading, isGuest, signOut: authSignOut } = useAuth();
  const navigate = useNavigate();
  const [mem, setMem] = useState<UserMemory>(loadMemory);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [cloudStats, setCloudStats] = useState({ decisionsCount: 0, watchlistCount: 0, unseenAlerts: 0 });
  const [reorderCount, setReorderCount] = useState(0);

  const { plan: currentPlan } = usePlan();
  const progression = getProgressionLevel();
  const profile = getBuyingProfile();
  const activity = getActivitySummary();
  const savings = getSavingsInsight();
  const streak = getStreak();
  const riskAvoidance = getRiskAvoidance();
  const behavior = analyzeBehavior();

  const isSignedIn = !!user;

  // Load cloud stats for signed-in users
  useEffect(() => {
    if (user) {
      getAccountStats(user.id).then(setCloudStats);
      getReorderItems(user.id).then(items => setReorderCount(items.length));
      getProfile(user.id).then(({ data }) => {
        if (data) {
          const updates: Partial<UserMemory> = {};
          if (data.default_country) updates.country = data.default_country;
          if (data.default_currency) updates.currency = data.default_currency;
          if (data.default_priority) updates.preferredPriority = data.default_priority;
          if (data.local_preference) updates.localPreference = data.local_preference as UserMemory["localPreference"];
          if (Object.keys(updates).length > 0) {
            const next = { ...mem, ...updates };
            setMem(next);
            saveMemory(next);
          }
        }
      });
    }
  }, [user]);

  // Show signed-out state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isSignedIn && !isGuest && activity.totalDecisions === 0) {
    return (
      <div className="min-h-screen bg-background pt-safe pb-[72px]">
        <div className="max-w-md mx-auto px-5 pt-20 flex flex-col items-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
            <User className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-xl font-display font-bold text-foreground mb-2 text-center">Your account</h1>
          <p className="text-sm text-muted-foreground font-body text-center mb-8 max-w-[260px] leading-relaxed">
            Sign in to save decisions, track prices, and keep preferences across devices.
          </p>
          <div className="w-full space-y-2.5">
            <button onClick={() => navigate("/auth")} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-body font-medium text-sm hover:opacity-90 transition-opacity">
              <LogIn className="w-4 h-4" />
              Sign in
            </button>
            <button onClick={() => navigate("/auth")} className="w-full py-3 rounded-xl border border-border/60 text-foreground font-body font-medium text-sm hover:bg-muted/30 transition-colors">
              Create account
            </button>
            <button onClick={() => navigate("/")} className="w-full py-2.5 rounded-xl text-muted-foreground font-body text-sm hover:text-foreground transition-colors">
              Continue as guest
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground/60 font-body mt-6 text-center">
            Guest data is stored locally and may be lost if you clear your browser.
          </p>
        </div>
      </div>
    );
  }

  const isGuestUser = !isSignedIn && (isGuest || activity.totalDecisions > 0);

  const planConfig: Record<string, { label: string; icon: typeof Shield; className: string }> = {
    guest: { label: "Guest", icon: Shield, className: "text-muted-foreground" },
    free: { label: "Free Plan", icon: Shield, className: "text-muted-foreground" },
    pro: { label: "Pro Plan", icon: Zap, className: "text-primary" },
    elite: { label: "Elite Plan", icon: Crown, className: "text-premium-accent" },
  };

  const effectivePlanKey = isGuestUser ? "guest" : currentPlan;

  const update = (partial: Partial<UserMemory>) => {
    const next = { ...mem, ...partial };
    setMem(next);
    saveMemory(next);
    // Sync to cloud if signed in
    if (user) {
      const cloudUpdates: Record<string, string | undefined> = {};
      if (partial.country) cloudUpdates.default_country = partial.country;
      if (partial.currency) cloudUpdates.default_currency = partial.currency;
      if (partial.preferredPriority) cloudUpdates.default_priority = partial.preferredPriority;
      if (partial.localPreference) cloudUpdates.local_preference = partial.localPreference;
      if (Object.keys(cloudUpdates).length > 0) {
        updateProfile(user.id, cloudUpdates);
      }
    }
  };

  const handleSignOut = async () => {
    await authSignOut();
    toast({ title: "Signed out", description: "Your local data is still available." });
  };

  const totalDecisions = isSignedIn ? Math.max(cloudStats.decisionsCount, activity.totalDecisions) : activity.totalDecisions;
  const totalTracked = isSignedIn ? Math.max(cloudStats.watchlistCount, activity.productsTracked) : activity.productsTracked;

  return (
    <div className="min-h-screen bg-background pt-safe pb-[100px] md:pb-[72px]">
      <div className="max-w-2xl mx-auto px-5 pt-8">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-display font-bold text-foreground">Profile</h1>
          <div className="flex items-center gap-2">
            {isSignedIn && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-score-high/12 text-score-high border border-score-high/20 font-body font-medium">
                Signed in
              </span>
            )}
            {!isSignedIn && (isGuest || activity.totalDecisions > 0) && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/50 font-body font-medium">
                Guest
              </span>
            )}
          </div>
        </div>

        {/* === ACCOUNT === */}
        <p className="text-[10px] text-muted-foreground/40 uppercase tracking-wider mt-2 mb-2 font-body">Account</p>

        {/* Account summary */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
          {isSignedIn && user?.email && (
            <p className="text-xs text-muted-foreground font-body mb-2.5">{user.email}</p>
          )}
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <p className="text-lg font-display font-bold text-foreground">{totalDecisions}</p>
              <p className="text-[10px] text-muted-foreground font-body">Decisions</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-display font-bold text-foreground">{totalTracked}</p>
              <p className="text-[10px] text-muted-foreground font-body">Tracked</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-display font-bold text-accent">{savings.totalSaved > 0 ? `$${savings.totalSaved}` : "—"}</p>
              <p className="text-[10px] text-muted-foreground font-body">Saved</p>
            </div>
          </div>
          {!isSignedIn && (
            <button
              onClick={() => navigate("/auth")}
              className="w-full py-2 rounded-lg bg-primary/10 text-primary text-xs font-body font-medium hover:bg-primary/15 transition-colors"
            >
              Sign in to keep your data across devices
            </button>
          )}
        </motion.div>

        {/* Plan */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
          <p className="text-[10px] text-muted-foreground font-body mb-2 uppercase tracking-wider">Current plan</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(() => { const PC = planConfig[effectivePlanKey]; return <PC.icon className={`w-4 h-4 ${PC.className}`} />; })()}
              <span className={`text-sm font-display font-bold ${planConfig[effectivePlanKey].className}`}>
                {planConfig[effectivePlanKey].label}
              </span>
            </div>
            <button onClick={() => setUpgradeOpen(true)} className="text-[11px] font-body font-medium text-primary hover:underline">
              See plans
            </button>
          </div>
        </motion.div>

        {/* === INSIGHTS === */}
        <p className="text-[10px] text-muted-foreground/40 uppercase tracking-wider mt-6 mb-2 font-body">Insights</p>

        {/* Impact */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-accent" />
            <span className="text-sm font-display font-semibold text-foreground">Your impact</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-lg font-display font-bold text-accent">
                {savings.totalSaved > 0 ? `$${savings.totalSaved}` : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground font-body">
                {savings.totalSaved > 0 ? "Estimated saved" : "Savings build over time"}
              </p>
            </div>
            <div>
              <p className="text-lg font-display font-bold text-foreground">
                {riskAvoidance.count > 0 ? riskAvoidance.count : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground font-body">
                {riskAvoidance.count > 0 ? "Risks avoided" : "Risk tracking active"}
              </p>
            </div>
            <div>
              <p className="text-lg font-display font-bold text-foreground flex items-center gap-1">
                {streak.currentStreak > 0 ? (
                  <><Flame className="w-4 h-4 text-score-mid" />{streak.currentStreak}</>
                ) : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground font-body">
                {streak.currentStreak > 0 ? "Day streak" : "Use daily to build streak"}
              </p>
            </div>
          </div>
          {savings.vsAverageBuyer && (
            <p className="text-[11px] text-accent/80 font-body border-t border-border/30 pt-2.5 mt-3">{savings.vsAverageBuyer}</p>
          )}
        </motion.div>

        {/* Progression */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <Shield className={`w-4 h-4 ${levelColors[progression.level]}`} />
              <span className={`text-sm font-display font-bold ${levelColors[progression.level]}`}>
                {progression.level}
              </span>
            </div>
            {progression.next && (
              <span className="text-[10px] text-muted-foreground font-body">Next: {progression.next}</span>
            )}
          </div>
          <div className="h-1 bg-muted/40 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progression.progress}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className="h-full rounded-full bg-primary" />
          </div>
          {progression.next && (
            <p className="text-xs text-primary/80 font-body mt-2">1 more decision to unlock {progression.next}</p>
          )}
        </motion.div>

        {/* Personalization engine */}
        {behavior.decisionsAnalyzed >= 3 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                <span className="text-sm font-display font-semibold text-foreground">How Decyzon learns you</span>
              </div>
              <button
                onClick={() => {
                  const reset = loadMemory();
                  reset.preferredPriority = undefined;
                  reset.localPreference = "adaptive";
                  saveMemory(reset);
                  setMem(reset);
                  toast({ title: "Preferences reset", description: "Your learned preferences have been cleared." });
                }}
                className="flex items-center gap-1 text-[10px] text-muted-foreground font-body hover:text-foreground transition-colors"
              >
                <RotateCcw className="w-2.5 h-2.5" />Reset
              </button>
            </div>
            
            {/* Personalization rules */}
            {(() => {
              const explanations = getPersonalizationExplanation();
              if (explanations.length === 0) return null;
              return (
                <div className="space-y-1 mb-3">
                  {explanations.map((exp, i) => (
                    <p key={i} className="text-[11px] font-body text-muted-foreground">
                      <span className="text-foreground">•</span> {exp}
                    </p>
                  ))}
                </div>
              );
            })()}

            {/* Category affinity */}
            {(() => {
              const affinity = getCategoryAffinity();
              if (affinity.length === 0) return null;
              const maxCount = affinity[0].count;
              return (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-muted-foreground/60 font-body uppercase tracking-wider">Top categories</p>
                  {affinity.map((a) => (
                    <div key={a.category} className="flex items-center gap-2">
                      <span className="text-[10px] font-body text-muted-foreground w-20 truncate capitalize">{a.category}</span>
                      <div className="flex-1 h-[3px] bg-muted/30 rounded-full overflow-hidden">
                        <div className="h-full bg-primary/60 rounded-full" style={{ width: `${(a.count / maxCount) * 100}%` }} />
                      </div>
                      <span className="text-[10px] font-body text-muted-foreground/60 w-4 text-right">{a.count}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            
            <div className="space-y-1.5 text-[11px] font-body text-muted-foreground mt-3 pt-2.5 border-t border-border/20">
              <p>Price sensitivity: <span className="text-foreground capitalize">{behavior.priceSensitivity}</span></p>
              <p>Speed preference: <span className="text-foreground capitalize">{behavior.speedPreference}</span></p>
              <p>Risk tolerance: <span className="text-foreground capitalize">{behavior.riskTolerance}</span></p>
              {behavior.autoAdjusted && (
                <p className="text-primary/70 text-[10px] mt-1.5">{behavior.adjustmentLabel}</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Buying profile */}
        {profile && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
            <div className="flex items-center gap-2 mb-2.5">
              <Target className="w-4 h-4 text-accent" />
              <span className="text-sm font-display font-semibold text-foreground">Buying profile</span>
            </div>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">{profile.insight}</p>
            {profile.topCategory && (
              <p className="text-[11px] text-muted-foreground font-body mt-1.5">Top category: <span className="text-foreground capitalize">{profile.topCategory}</span></p>
            )}
          </motion.div>
        )}

        {/* Activity card removed — duplicate of Account summary */}

        {/* === REORDER === */}
        <ReorderSection />

        {/* === SETTINGS === */}
        <p className="text-[10px] text-muted-foreground/40 uppercase tracking-wider mt-6 mb-2 font-body">Settings</p>

        {/* Alert preferences */}
        <AlertPreferences />

        {/* Preferences */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-display font-semibold text-foreground">Preferences</span>
          </div>

          <div className="mb-4">
            <label className="text-[10px] text-muted-foreground font-body mb-2 block uppercase tracking-wider">Country</label>
            <div className="grid grid-cols-2 gap-1.5">
              {countries.map((c) => (
                <button
                  key={c.code}
                  onClick={() => update({ country: c.label, region: c.code, currency: c.currency })}
                  className={`px-3 py-2 rounded-lg border text-[11px] font-body transition-colors ${
                    mem.region === c.code
                      ? "border-primary/30 bg-primary/10 text-primary"
                      : "border-border/50 bg-secondary/60 text-secondary-foreground hover:border-primary/20"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="text-[10px] text-muted-foreground font-body mb-2 block uppercase tracking-wider">Default priority</label>
            <div className="grid grid-cols-2 gap-1.5">
              {priorities.map((p) => (
                <button
                  key={p.value}
                  onClick={() => update({ preferredPriority: p.value })}
                  className={`px-3 py-2 rounded-lg border text-[11px] font-body transition-colors ${
                    mem.preferredPriority === p.value
                      ? "border-primary/30 bg-primary/10 text-primary"
                      : "border-border/50 bg-secondary/60 text-secondary-foreground hover:border-primary/20"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] text-muted-foreground font-body mb-2 block uppercase tracking-wider">Route preference</label>
            <div className="grid grid-cols-3 gap-1.5">
              {biases.map((b) => (
                <button
                  key={b.value}
                  onClick={() => update({ localPreference: b.value as UserMemory["localPreference"] })}
                  className={`px-3 py-2 rounded-lg border text-[11px] font-body transition-colors ${
                    mem.localPreference === b.value
                      ? "border-primary/30 bg-primary/10 text-primary"
                      : "border-border/50 bg-secondary/60 text-secondary-foreground hover:border-primary/20"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Account actions */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.23 }} className="rounded-xl border border-border/50 bg-card p-4">
          <div className="space-y-1">
            {isSignedIn && (
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-body text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors text-left"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            )}
            {!isSignedIn && (
              <button
                onClick={() => navigate("/auth")}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-body text-primary hover:bg-primary/10 transition-colors text-left"
              >
                <LogIn className="w-4 h-4" />
                Sign in to save your data
              </button>
            )}
          </div>
        </motion.div>

        {/* Legal links (mobile — footer is hidden on mobile) */}
        <div className="md:hidden mt-8 mb-4 flex flex-wrap items-center justify-center gap-4">
          {[
            { to: "/privacy", label: "Privacy" },
            { to: "/terms", label: "Terms" },
            { to: "/refunds", label: "Refunds" },
            { to: "/contact", label: "Contact" },
          ].map((link) => (
            <Link key={link.to} to={link.to} className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors font-body">
              {link.label}
            </Link>
          ))}
        </div>

        <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
      </div>
    </div>
  );
}
